import React, { useEffect,useState } from "react";
import Header from "./Header/Header";
import "./AnalyticsScreen.scss";
import BasicReports from "./BasicReports/BasicReports";
import { fetchBasicAnalyticsRequest } from "../../../redux/exam_analytics/analyticsSlice";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import { useLocation,useHistory  } from "react-router-dom";

const AnalyticsScreen = () => {
  const dispatch = useDispatch();
const history = useHistory();
const { testDetails } = useSelector((state) => state.examSlice);
const source = testDetails?.source;
  const [attempt, setAttempt] = useState(0);
  const [isAttemptInitialized, setIsAttemptInitialized] = useState(false);
  const [scoreRetryCount, setScoreRetryCount] = useState(0);
  const [latestAttemptSynced, setLatestAttemptSynced] = useState(false);

  const { basicAnalytics, loading, error } = useSelector(
    (s) => s.analyticsSlice
  );
const location = useLocation();
const routePaperType = location.state?.paper_type;
const routeSetId = location.state?.set_id;
const routePaperId = location.state?.paper_id;
const routePlannerTestId = location.state?.planner_test_id;
const isFromSubmit = Boolean(location.state?.from_submit);

const extractAttemptCountFromTitle = (title) => {
  const match = String(title || "").match(/\d+/);
  return match ? Number(match[0]) : 0;
};

const getLatestAttemptMeta = (attempts = []) => {
  if (!Array.isArray(attempts) || attempts.length === 0) return null;

  return attempts.reduce((best, item) => {
    const currentCount = extractAttemptCountFromTitle(item?.title);
    const bestCount = extractAttemptCountFromTitle(best?.title);
    if (currentCount > bestCount) return item;
    if (currentCount === bestCount && Number(item?.id) > Number(best?.id)) {
      return item;
    }
    return best;
  }, attempts[0]);
};

const getRequestPayload = () => {
  if (routeSetId) {
    return {
      paper_type: routePaperType,
      set_id: routeSetId,
    };
  }

  if (routePaperId) {
    return {
      paper_type: routePaperType,
      paper_id: routePaperId,
      planner_test_id: routePlannerTestId,
    };
  }

  return {};
};

useEffect(() => {
  const requestPayload = getRequestPayload();

  const dispatchFetch = () => {
    dispatch(fetchBasicAnalyticsRequest(requestPayload));
  };

  if (isFromSubmit) {
    const timerId = setTimeout(dispatchFetch, 1500);
    return () => clearTimeout(timerId);
  }

  dispatchFetch();
}, [
  dispatch,
  location.state?.from_submit,
  location.state?.paper_type,
  location.state?.set_id,
  location.state?.paper_id,
  location.state?.planner_test_id,
]);

const apiData = basicAnalytics?.data;
useEffect(() => {
  if (!apiData || isAttemptInitialized) return;
  const attemptsList = apiData?.attempts || [];
  if (!attemptsList.length) return;

  const latestAttempt = getLatestAttemptMeta(attemptsList);
  const apiAttemptCount = Number(apiData.selected_attempt);

  // Backend selected_attempt is attempt_count (1-based), while dropdown uses attempt id.
  const byTitle = attemptsList.find(
    (item) => Number(item?.title?.match(/\d+/)?.[0]) === apiAttemptCount
  );
  const byId = attemptsList.find((item) => Number(item?.id) === apiAttemptCount);
  const byIndex = attemptsList[apiAttemptCount - 1];

  const resolvedAttemptId = Number(
    byTitle?.id ?? byId?.id ?? byIndex?.id ?? attemptsList[0]?.id ?? 0
  );

  // For submit flow, always default to latest attempt.
  setAttempt(isFromSubmit ? Number(latestAttempt?.id ?? resolvedAttemptId) : resolvedAttemptId);
  setIsAttemptInitialized(true);
}, [apiData, isAttemptInitialized, isFromSubmit]);

useEffect(() => {
  if (!apiData?.attempts?.length) {
    setIsAttemptInitialized(false);
  }
}, [apiData?.attempts]);

useEffect(() => {
  // Reset latest-attempt sync when entering analytics from submit.
  setLatestAttemptSynced(false);
  setScoreRetryCount(0);
}, [isFromSubmit, routePaperType, routePaperId, routeSetId, routePlannerTestId]);

useEffect(() => {
  if (!isFromSubmit || !apiData || latestAttemptSynced) return;
  const attemptsList = apiData?.attempts || [];
  if (!attemptsList.length) return;

  const latestAttempt = getLatestAttemptMeta(attemptsList);
  if (!latestAttempt) return;

  const latestAttemptId = Number(latestAttempt?.id);
  const selectedAttemptId = Number(apiData?.selected_attempt || 0);

  // If backend still points to old selected_attempt, force fetch latest once.
  if (Number.isFinite(latestAttemptId) && selectedAttemptId !== latestAttemptId) {
    setAttempt(latestAttemptId);
    dispatch(
      fetchBasicAnalyticsRequest({
        ...getRequestPayload(),
        attempt_count: latestAttemptId,
      })
    );
  }

  setLatestAttemptSynced(true);
}, [dispatch, isFromSubmit, apiData, latestAttemptSynced]);

const totalAnsweredMeta =
  Number(apiData?.total_correct ?? 0) +
  Number(apiData?.total_incorrect ?? 0) +
  Number(apiData?.total_un_attempt ?? 0);

const isScoreReady =
  Boolean(apiData) &&
  (
    (apiData.user_marks !== null && apiData.user_marks !== undefined) ||
    (apiData.total_marks !== null && apiData.total_marks !== undefined) ||
    totalAnsweredMeta > 0 ||
    Number(apiData?.is_resultopen) === 1
  );

useEffect(() => {
  if (!isFromSubmit) return;
  if (!apiData || isScoreReady || loading?.basic) return;
  if (scoreRetryCount >= 5) return;

  const timerId = setTimeout(() => {
    dispatch(fetchBasicAnalyticsRequest(getRequestPayload()));
    setScoreRetryCount((prev) => prev + 1);
  }, 2000);

  return () => clearTimeout(timerId);
}, [
  dispatch,
  isFromSubmit,
  apiData,
  isScoreReady,
  loading?.basic,
  scoreRetryCount,
  location.state?.paper_type,
  location.state?.set_id,
  location.state?.paper_id,
  location.state?.planner_test_id,
]);

const handleAttemptChange = (nextAttempt) => {
  if (nextAttempt === attempt) return;

  setAttempt(nextAttempt);
  setLatestAttemptSynced(true);

  const payload = {
    attempt_count: Number(nextAttempt),
  };

  // When available, pass explicit route params; otherwise saga falls back to examSlice state.
  if (location.state?.set_id) {
    payload.paper_type = location.state.paper_type;
    payload.set_id = location.state.set_id;
  } else if (location.state?.paper_id) {
    payload.paper_type = location.state.paper_type;
    payload.paper_id = location.state.paper_id;
    payload.planner_test_id = location.state.planner_test_id;
  }

  dispatch(fetchBasicAnalyticsRequest(payload));
};


 
  if (loading?.basic) {
    return (
      <div className="analytics-loader">
        <ClipLoader color="#C61C26" size={55} />
      </div>
    );
  }

 
  if (error?.basic) {
    return (
      <div className="analytics-error">
        <p>Error: {error.basic}</p>
      </div>
    );
  }

  return (
    <div className="analytics-wrapper">
      {/* <Header paperName={apiData?.paper_name} />
      <BasicReports basicAnalytics={apiData} /> */}
      {/* <Header paperName={apiData?.paper_name} /> */}
      <Header
  paperName={apiData?.paper_name}
    attempts={apiData?.attempts}
  selectedAttempt={attempt}
  onAttemptChange={handleAttemptChange}
  onBack={() => {
    if (source === "topicwise") {
      history.push("/practice/topic-wise/pyqset");
    } else if (source === "yearwise") {
      history.push("/practice/paper-listing?type=Year Wise");
    } else {
      history.push("/practice/paper-listing");
    }
  }}
/>

{isScoreReady ? (
  <BasicReports basicAnalytics={apiData} />
) : (
  <div className="analytics-loader">
    <ClipLoader color="#C61C26" size={55} />
    <p>Preparing your scorecard...</p>
  </div>
)}

    </div>
  );
};

export default AnalyticsScreen;
